import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  ViewEncapsulation,
  OnInit,
  OnChanges,
  Optional,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { Locale } from 'date-fns';

import { ISelectedRange } from '../../date-picker/utils/types';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../../locale/phx-locale.token';
import { PhxTranslationService } from '../../../translation/translation.service';
import { mapToDateFnsLocale } from '../../date-picker/utils/helpers';
import { dateFormatter } from '../../../utils';

@Component({
  selector: 'phx-month-picker-body',
  templateUrl: './phx-month-picker-body.component.html',
  styleUrls: ['./phx-month-picker-body.component.scss'],
  host: {
    class: 'phx-month-picker-body',
    '[tabIndex]': '0',
    '(focus)': 'onWrapperFocus()',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxMonthPickerBodyComponent implements OnInit, OnChanges {
  @Input() public phxIsRangeEnabled: boolean = false;
  @Input() public phxValue: Date | ISelectedRange | null = null;
  @Input() public phxYear: number = new Date().getFullYear();
  @Input() public phxShowSelection: boolean = true;

  @Output() public phxValueChange = new EventEmitter<Date | ISelectedRange | null>();

  public months: Date[] = [];
  public monthNames: string[] = [];
  public selectedStart: Date | null = null;
  public selectedEnd: Date | null = null;
  public hoveredMonth: Date | null = null;
  public hoverDirection: 'left' | 'right' | null = null;

  focusedMonthIndex: number = 0;

  constructor(
    private translationService: PhxTranslationService,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) {}

  private getDateFnsLocale(): Locale {
    return this.phxLocale
      ? this.phxLocale.getDateFnsLocale()
      : mapToDateFnsLocale(this.translationService.getLocale());
  }

  public formatMonth(date: Date): string {
    if (this.phxLocale?.formatMonth) {
      return this.phxLocale.formatMonth(date);
    }
    return dateFormatter(date, 'MMM', this.getDateFnsLocale());
  }

  handleKeyboardEvent(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowLeft':
        this.focusPreviousMonth();
        break;
      case 'ArrowRight':
        this.focusNextMonth();
        break;
    }
  }

  public ngOnInit(): void {
    this.generateMonths(this.phxYear);
  }

  public onWrapperFocus(): void {
    this.focusedMonthIndex = 0;
    this.updateFocus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxValue, phxIsRangeEnabled, phxYear } = changes;
    if (phxIsRangeEnabled && phxIsRangeEnabled.currentValue) {
      this.selectedEnd = null;
      this.hoveredMonth = null;
      this.hoverDirection = null;
    }
    if (phxValue) {
      this.setupValue();
    }
    if (phxYear && phxYear?.currentValue) {
      this.generateMonths(this.phxYear);
    }
  }

  private generateMonths(year: number): void {
    this.months = [];
    for (let i = 0; i < 12; i++) {
      this.months.push(new Date(year, i, 1));
    }
    this.monthNames = this.months.map(m => this.formatMonth(m));
  }

  private setupValue(): void {
    if (!this.phxIsRangeEnabled) {
      if (this.phxValue && this.phxValue instanceof Date) {
        this.selectedStart = new Date(this.phxValue.getFullYear(), this.phxValue.getMonth(), 1);
      } else {
        this.selectedStart = null;
      }
    } else {
      if (this.phxValue) {
        if ('start' in this.phxValue && this.phxValue?.start) {
          this.selectedStart = new Date(this.phxValue.start.getFullYear(), this.phxValue.start.getMonth(), 1);
        } else {
          this.selectedStart = null;
        }

        if ('end' in this.phxValue && this.phxValue?.end) {
          this.selectedEnd = new Date(this.phxValue.end.getFullYear(), this.phxValue.end.getMonth(), 1);
        } else {
          this.selectedEnd = null;
        }
      } else {
        this.selectedStart = null;
        this.selectedEnd = null;
      }
    }
  }

  public selectMonth(month: Date): void {
    if (!this.phxIsRangeEnabled) {
      this.phxValueChange.emit(month);
      return;
    }

    if (!this.selectedStart) {
      // First selection
      this.selectedStart = month;
      this.phxValueChange.emit({ start: month, end: null });
    } else if (!this.selectedEnd) {
      // Second selection
      let startingMonth: Date = this.selectedStart;
      let endingMonth: Date = month;

      if (month < this.selectedStart) {
        startingMonth = month;
        endingMonth = this.selectedStart;
      }

      this.selectedStart = startingMonth;
      this.selectedEnd = endingMonth;
      this.phxValueChange.emit({ start: startingMonth, end: endingMonth });
      this.hoverDirection = null;
    } else {
      // Reset and start new selection
      this.selectedStart = month;
      this.selectedEnd = null;
      this.phxValueChange.emit({ start: month, end: null });
    }
  }

  public hoverMonth(month: Date): void {
    if (!this.phxIsRangeEnabled) return;
    if (this.selectedStart && !this.selectedEnd) {
      this.hoveredMonth = month;
      this.hoverDirection = month < this.selectedStart ? 'left' : 'right';
    } else {
      this.hoveredMonth = null;
      this.hoverDirection = null;
    }
  }

  public leaveMonth(): void {
    this.hoveredMonth = null;
    this.hoverDirection = null;
  }

  public isSelectedMonth(month: Date): boolean {
    if (this.phxIsRangeEnabled) {
      return (
        (this.selectedStart &&
          month.getFullYear() === this.selectedStart.getFullYear() &&
          month.getMonth() === this.selectedStart.getMonth()) ||
        (this.selectedEnd &&
          month.getFullYear() === this.selectedEnd.getFullYear() &&
          month.getMonth() === this.selectedEnd.getMonth())
      );
    }
    return (
      this.selectedStart &&
      month.getFullYear() === this.selectedStart.getFullYear() &&
      month.getMonth() === this.selectedStart.getMonth()
    );
  }

  public isTodayMonth(month: Date): boolean {
    const now: Date = new Date();
    return month.getFullYear() === now.getFullYear() && month.getMonth() === now.getMonth();
  }

  public isInSelectedRange(month: Date): boolean | undefined {
    if (!this.phxIsRangeEnabled) return;
    return this.selectedStart && this.selectedEnd && month >= this.selectedStart && month <= this.selectedEnd;
  }

  public isInHoveredRange(month: Date): boolean | undefined {
    if (!this.phxIsRangeEnabled) return;
    if (this.selectedStart && !this.selectedEnd && this.hoveredMonth) {
      const start: Date = this.selectedStart < this.hoveredMonth ? this.selectedStart : this.hoveredMonth;
      const end: Date = this.selectedStart > this.hoveredMonth ? this.selectedStart : this.hoveredMonth;
      return month >= start && month <= end;
    }
    return false;
  }

  public isRangeStart(month: Date): boolean | undefined {
    if (!this.phxIsRangeEnabled) return;
    return (
      this.selectedStart &&
      this.selectedStart.getFullYear() === month.getFullYear() &&
      this.selectedStart.getMonth() === month.getMonth()
    );
  }

  public isRangeEnd(month: Date): boolean | undefined {
    if (!this.phxIsRangeEnabled) return;
    return (
      this.selectedEnd &&
      this.selectedEnd.getFullYear() === month.getFullYear() &&
      this.selectedEnd.getMonth() === month.getMonth()
    );
  }

  public isRangeSame(month: Date): boolean | undefined {
    if (!this.phxIsRangeEnabled) return;
    return this.isRangeEnd(month) && this.isRangeStart(month);
  }

  public setDisabledState?(isDisabled: boolean): void {
    // Handle the disabled state if needed
  }

  focusPreviousMonth() {
    // Logic to focus the previous month
    this.focusedMonthIndex = (this.focusedMonthIndex - 1 + 12) % 12;
    this.updateFocus();
  }

  focusNextMonth() {
    // Logic to focus the next month
    this.focusedMonthIndex = (this.focusedMonthIndex + 1) % 12;
    this.updateFocus();
  }

  updateFocus() {
    const monthElements = document.querySelectorAll('.phx-month-picker-month-cell');
    if (monthElements[this.focusedMonthIndex]) {
      (monthElements[this.focusedMonthIndex] as HTMLElement).focus();
      this.hoverMonth(this.months[this.focusedMonthIndex]);
    }
  }
}
